import { useState, useEffect, useRef } from "react";
import { Reveal } from "@/components/common/Reveal";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useArticles } from "@/lib/insights";

const NAV_LINKS = [
  { label: "Articles", hash: "#articles", dotColor: "bg-emerald-500" },
  { label: "Subscribe", hash: "#subscribe", dotColor: "bg-sky-500" },
];

const FAKE_FEED_LINES = [
  { time: "NOW", status: "DRAFTING" as const, text: "How COSX agents handle compliance at scale" },
  { time: "REVIEW", status: "REVIEW" as const, text: "Four-layer safety: how Robolab X protects children online" },
  { time: "REVIEW", status: "REVIEW" as const, text: "Data residency: what it means and why it matters" },
  { time: "QUEUED", status: "QUEUED" as const, text: "Why we chose to build, not buy" },
  { time: "QUEUED", status: "QUEUED" as const, text: "The economics of AI in regulated industries" },
  { time: "QUEUED", status: "QUEUED" as const, text: "Inside our compliance-first architecture" },
  { time: "QUEUED", status: "QUEUED" as const, text: "What firm acquisition means for AI deployment" },
];

const STATUS_STYLE = {
  PUBLISHED: "text-emerald-500",
  DRAFTING: "text-amber-500",
  REVIEW: "text-sky-500",
  QUEUED: "text-[var(--color-fg-tertiary)]",
};

// Typewriter for the DRAFTING line
function DraftingLine({ text }: { text: string }) {
  const prefersReducedMotion = useReducedMotion();
  const [charIndex, setCharIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (prefersReducedMotion) return;
    if (charIndex < text.length) {
      timerRef.current = setTimeout(() => setCharIndex((c) => c + 1), 60);
    } else {
      // Reset after pause
      timerRef.current = setTimeout(() => setCharIndex(0), 4000);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [charIndex, text, prefersReducedMotion]);

  return (
    <span>
      {prefersReducedMotion ? text : text.slice(0, charIndex)}
      {!prefersReducedMotion && (
        <span className="inline-block w-[2px] h-[1em] bg-amber-500 ml-0.5 align-middle" style={{ animation: "cursorBlink 0.8s step-end infinite" }} />
      )}
    </span>
  );
}

export function InsightsHero() {
  const prefersReducedMotion = useReducedMotion();
  const articles = useArticles().slice(0, 10);

  const publishedLines = articles.map((a) => ({
    time: new Date(a.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }).toUpperCase(),
    status: "PUBLISHED" as const,
    text: a.title,
  }));

  // Duplicate for seamless marquee
  const marqueeItems = [...publishedLines, ...publishedLines, ...publishedLines];

  return (
    <section className="relative z-10 flex flex-col items-center pt-[var(--topnav-height)] pb-0 overflow-hidden">
      <div className="max-w-6xl mx-auto w-full text-center pt-16 mb-10 px-8">
        <Reveal>
          <h1 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
            Insights
          </h1>
        </Reveal>
        <Reveal delay={100}>
          <p className="text-lg text-[var(--color-fg-secondary)] max-w-2xl mx-auto font-light mb-10">
            Thinking, updates, and announcements from the COSX team.
          </p>
        </Reveal>
        <Reveal delay={200}>
          <nav className="flex flex-wrap justify-center gap-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.hash}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] border-b border-[var(--color-border)] hover:border-[var(--color-fg-secondary)] transition-colors"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${link.dotColor}`} />
                <span>{link.label}</span>
              </a>
            ))}
          </nav>
        </Reveal>
      </div>

      {/* Editorial control room strip */}
      <Reveal delay={300}>
        <div className="w-screen relative overflow-hidden mt-8 md:mt-12 mb-8 md:mb-12 hero-strip-mask">
          <div className="group/console bg-[var(--color-surface)] border-y border-[var(--color-border)] my-3">
            {/* Row 1: PUBLISHED — scrolling left */}
            <div className="overflow-hidden h-[32px] flex items-center border-b border-[var(--color-border)]/50">
              <div className={`flex items-center w-max whitespace-nowrap ${prefersReducedMotion ? "" : "animate-[marqueeLeft_50s_linear_infinite] group-hover/console:[animation-play-state:paused]"}`}>
                {marqueeItems.map((line, i) => (
                  <span key={i} className="flex items-center gap-3 px-6 text-[11px] font-[family-name:var(--font-mono)]">
                    <span className="text-[var(--color-fg-tertiary)] tabular-nums">{line.time}</span>
                    <span className="text-emerald-500 text-[9px]">✓ PUBLISHED</span>
                    <span className="text-[var(--color-fg-secondary)]">{line.text}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Row 2: DRAFTING — typewriter, centered */}
            <div className="flex items-center justify-center gap-3 px-6 h-[36px] border-b border-[var(--color-border)]/50">
              <span className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] tabular-nums">NOW</span>
              <span className={`text-[9px] font-[family-name:var(--font-mono)] tracking-widest uppercase ${STATUS_STYLE.DRAFTING}`}>DRAFTING</span>
              <span className="text-[11px] font-[family-name:var(--font-mono)] text-[var(--color-fg)]">
                <DraftingLine text={FAKE_FEED_LINES.find((l) => l.status === "DRAFTING")?.text ?? ""} />
              </span>
            </div>

            {/* Row 3: IN REVIEW — scrolling right */}
            <div className="overflow-hidden h-[32px] flex items-center border-b border-[var(--color-border)]/50">
              <div className={`flex items-center w-max whitespace-nowrap ${prefersReducedMotion ? "" : "animate-[marqueeRight_55s_linear_infinite] group-hover/console:[animation-play-state:paused]"}`}>
                {[...FAKE_FEED_LINES.filter((l) => l.status === "REVIEW"), ...FAKE_FEED_LINES.filter((l) => l.status === "REVIEW"), ...FAKE_FEED_LINES.filter((l) => l.status === "REVIEW"), ...FAKE_FEED_LINES.filter((l) => l.status === "REVIEW")].map((line, i) => (
                  <span key={i} className="flex items-center gap-3 px-6 text-[11px] font-[family-name:var(--font-mono)]">
                    <span className="text-[var(--color-fg-tertiary)] tabular-nums">{line.time}</span>
                    <span className="text-sky-500 text-[9px]">◎ REVIEW</span>
                    <span className="text-[var(--color-fg-secondary)] opacity-70">{line.text}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Row 4: QUEUED — scrolling left, slowest */}
            <div className="overflow-hidden h-[32px] flex items-center">
              <div className={`flex items-center w-max whitespace-nowrap ${prefersReducedMotion ? "" : "animate-[marqueeLeft_70s_linear_infinite] group-hover/console:[animation-play-state:paused]"}`}>
                {[...FAKE_FEED_LINES.filter((l) => l.status === "QUEUED"), ...FAKE_FEED_LINES.filter((l) => l.status === "QUEUED"), ...FAKE_FEED_LINES.filter((l) => l.status === "QUEUED"), ...FAKE_FEED_LINES.filter((l) => l.status === "QUEUED")].map((line, i) => (
                  <span key={i} className="flex items-center gap-3 px-6 text-[11px] font-[family-name:var(--font-mono)]">
                    <span className="text-[var(--color-fg-tertiary)] tabular-nums">{line.time}</span>
                    <span className="text-[var(--color-fg-tertiary)] text-[9px]">· QUEUED</span>
                    <span className="text-[var(--color-fg-tertiary)]">{line.text}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
